import React, { useState } from 'react'
import { Form, Formik } from 'formik'
import useTrans from '@hooks/useTrans'
import useUtente from '@hooks/useUtente'
import { Button } from '@components/atoms'
import { CheckoutFormIndirizzoBlock } from '@components/molecules'
import styles from './CheckoutFormIndirizzo.module.sass'
import { getAddressValidationSchema } from '@utils/validationSchemas'
import { useValidaIndirizzoMutation, useValidaCodicePostaleMutation } from '@gql/graphql'

interface Props {
  variant?: 'spedizione' | 'fatturazione'
  onCompleted?: (v: any) => void
  className?: string
  onlyFatturazione?: boolean
}

const CheckoutFormIndirizzo = (props: Props) => {
  const {
    variant = 'spedizione',
    onCompleted = undefined,
    className = '',
    onlyFatturazione = false,
  } = props

  const t = useTrans()
  const { utente } = useUtente()
  const [validaIndirizzo] = useValidaIndirizzoMutation()
  const [validaCodicePostale] = useValidaCodicePostaleMutation()

  const [addAddressOpen, setAddAddressOpen] = useState({
    spedizione: false,
    fatturazione: false,
  })

  const indirizzoSpedizioneDefault = utente?.indirizziSpedizione.filter(
    (indirizzo) => indirizzo.isDefault
  )[0]

  const indirizzoFatturazioneDefault = utente?.indirizziFatturazione.filter(
    (indirizzo) => indirizzo.isDefault
  )[0]

  const initialValues = {
    indirizzoFatturazioneId: `${
      indirizzoFatturazioneDefault
        ? indirizzoFatturazioneDefault.pk
        : utente?.indirizziFatturazione?.[0]?.pk
    }`,
    nomeFatturazione: '',
    cognomeFatturazione: '',
    indirizzoFatturazione: '',
    informazioniAggiuntiveFatturazione: '',
    cittaFatturazione: '',
    statoFatturazione: '',
    capFatturazione: '',
    telefonoFatturazione: '',
    indirizzoSpedizioneId: `${
      indirizzoSpedizioneDefault
        ? indirizzoSpedizioneDefault.pk
        : utente?.indirizziSpedizione?.[0]?.pk
    }`,
    nomeSpedizione: '',
    cognomeSpedizione: '',
    indirizzoSpedizione: '',
    informazioniAggiuntiveSpedizione: '',
    cittaSpedizione: '',
    statoSpedizione: '',
    capSpedizione: '',
    telefonoSpedizione: '',
    stessoIndirizzo: true,
  }

  return (
    <div className={`${styles.root} ${className}`}>
      <Formik
        initialValues={initialValues}
        // validationSchema={getAddressValidationSchema(t, variant, addAddressOpen)}
        onSubmit={async (values, { setSubmitting, setFieldError }) => {
          const validateAddress = async (addressData: any, label: string, variantName: string) => {
            const { indirizzo, citta, provincia, cap, countryCode } = addressData
            const firstLetterUppercase = (value) => value.charAt(0).toUpperCase() + value.slice(1)

            try {
              const { data: capData } = await validaCodicePostale({
                variables: {
                  postalCode: cap,
                  countryCode: countryCode,
                  stateCode: provincia,
                  carrierCode: 'FDXE',
                },
              })

              if (!capData?.validaCodicePostale?.valid) {
                setFieldError(
                  `cap${firstLetterUppercase(variantName)}`,
                  t('Il codice postale non è valido')
                )
                return false
              }
            } catch (error) {
              setFieldError(
                `cap${firstLetterUppercase(variantName)}`,
                t('Errore durante la validazione del CAP')
              )
              return false
            }

            // Validazione indirizzo completo
            try {
              const { data: addressData } = await validaIndirizzo({
                variables: {
                  address: {
                    street: indirizzo,
                    city: citta,
                    postalCode: cap,
                    countryCode: countryCode,
                  },
                },
              })

              if (!addressData?.validaIndirizzo?.valid) {
                setFieldError(
                  `indirizzo${firstLetterUppercase(variantName)}`,
                  t("L'indirizzo non è valido o non è stato trovato")
                )
                return false
              }
            } catch (error) {
              setFieldError(
                `indirizzo${firstLetterUppercase(variantName)}`,
                t("Errore durante la validazione dell'indirizzo")
              )
              return false
            }

            return true
          }

          const getAddressData = (variantName: string) => {
            const firstLetterUppercase = (value) => value.charAt(0).toUpperCase() + value.slice(1)

            if (addAddressOpen[variantName]) {
              const cap = values[`cap${firstLetterUppercase(variantName)}`]
              const cittaProvincia = values[`citta${firstLetterUppercase(variantName)}`]
              const [citta, provincia] = cittaProvincia?.split('|') || []
              const indirizzo = values[`indirizzo${firstLetterUppercase(variantName)}`]
              const countryCode = values[`stato${firstLetterUppercase(variantName)}`] || 'IT'

              return { indirizzo, citta, provincia, cap, countryCode }
            }

            const indirizzoId = values[`indirizzo${firstLetterUppercase(variantName)}Id`]
            const indirizziList =
              variantName === 'spedizione'
                ? utente?.indirizziSpedizione
                : utente?.indirizziFatturazione
            const selectedIndirizzo = indirizziList?.find(
              (ind) => ind.pk.toString() === indirizzoId
            )

            if (selectedIndirizzo) {
              return {
                indirizzo: selectedIndirizzo.indirizzo,
                citta: selectedIndirizzo.citta,
                provincia: selectedIndirizzo.provincia,
                cap: selectedIndirizzo.cap,
                countryCode: selectedIndirizzo.nazione?.isoCode || 'IT',
              }
            }

            return null
          }

          if (variant === 'spedizione' || !onlyFatturazione) {
            const spedizioneData = getAddressData('spedizione')
            if (spedizioneData) {
              const isValid = await validateAddress(spedizioneData, 'spedizione', 'spedizione')
              if (!isValid) {
                setSubmitting(false)
                return
              }
            }
          }

          if (onlyFatturazione || !values.stessoIndirizzo) {
            const fatturazioneData = getAddressData('fatturazione')
            if (fatturazioneData) {
              const isValid = await validateAddress(
                fatturazioneData,
                'fatturazione',
                'fatturazione'
              )
              if (!isValid) {
                setSubmitting(false)
                return
              }
            }
          }

          values['onlyFatturazione'] = onlyFatturazione

          if (onCompleted) {
            await onCompleted(values)
            setSubmitting(false)
          }
        }}
      >
        {(formikProps) => (
          <Form className={styles.form}>
            <CheckoutFormIndirizzoBlock
              title={variant === 'spedizione' ? 'Indirizzo di spedizione' : undefined}
              variant={variant}
              formikProps={formikProps}
              addAddressOpen={addAddressOpen[variant]}
              setAddAddressOpen={(v) => setAddAddressOpen((prev) => ({ ...prev, [variant]: v }))}
            />
            {variant === 'spedizione' && !formikProps.values['stessoIndirizzo'] && (
              <CheckoutFormIndirizzoBlock
                title={t('Indirizzo di fatturazione')}
                variant={'fatturazione'}
                formikProps={formikProps}
                addAddressOpen={addAddressOpen['fatturazione']}
                setAddAddressOpen={(v) =>
                  setAddAddressOpen((prev) => ({ ...prev, fatturazione: v }))
                }
                className={styles.indirizzoBlock2}
              />
            )}
            {!addAddressOpen.spedizione && !addAddressOpen.fatturazione && (
              <div className={styles.ctaBox}>
                <Button
                  type="submit"
                  label={t('Procedi')}
                  size="lg"
                  loading={formikProps.isSubmitting}
                />
              </div>
            )}
          </Form>
        )}
      </Formik>
    </div>
  )
}

export default CheckoutFormIndirizzo
